/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The lists screen, the "from the plan" generator, and the non-destructive
 * regenerate flow.
 *
 * Lists come from the shopping engine (live, household-wide, with an
 * offline outbox): a list your partner makes on their phone appears here
 * within a second. Starting a list is blank by default — "Costco run",
 * "Friday dinner" — and the plan is one source among several, not the only
 * way in (docs/SHOPPING_LIST_10X.md §4.1).
 *
 * The plan keeps its zero-decision path: one button puts this week's
 * ingredients onto Home (staples skipped); other days and other lists are
 * one menu item away.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ShoppingApi } from "../../api/endpoints.ts";
import { ShoppingLiveApi } from "../../api/shoppingLive.ts";
import { addDays, formatDay, type YMD } from "../../lib/dates.ts";
import { useToday } from "../../lib/useToday.tsx";
import { ErrorBox, Loading, Modal, PageHeader } from "../../components/ui.tsx";
import { Icon, IconButton } from "../../components/Icon.tsx";
import { ActionMenu } from "../../components/ActionMenu.tsx";
import { useToast } from "../../components/Toast.tsx";
import type { RegenerateDiff } from "../../api/types.ts";
import type { LiveList } from "../../api/shoppingLive.ts";
import { LiveBadge, useShoppingLive, useUndoable } from "./live/useShoppingLive.tsx";
import {
  archivedLists, counts, homeListId, itemsOf, liveLists, memberName, sessionsOn, sharesOf, storeName,
  templateLists, type Model,
} from "./live/model.ts";
import { CategoriesModal, PlanMergeModal, StoresModal } from "./ShoppingManage.tsx";

type Sheet = "stores" | "categories" | "plan" | null;

/**
 * The lists screen (docs/SHOPPING_LIST_10X.md §4.1): Home pinned first, then
 * the family's own lists, newest activity first; templates; everything live.
 */
export function ShoppingListsView() {
  const { today } = useToday();
  const { model, status, error, unsent } = useShoppingLive();
  const { run, bar } = useUndoable(model);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<LiveList | null>(null);
  const [showArchive, setShowArchive] = useState(false);
  const [sheet, setSheet] = useState<Sheet>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  /**
   * The plan onto Home, with no questions: the server's today through
   * today+6, staples skipped (decision 1 — the plan merges into a list rather
   * than making a new one every week). `from`/`to` come from the Plan
   * screen's hand-off.
   */
  const quickMerge = useMutation({
    mutationFn: (range?: { from: YMD; to: YMD }) => ShoppingLiveApi.mergePlan({
      listId: "home",
      from: range?.from ?? today!,
      to: range?.to ?? addDays(today!, 6),
      applyStaples: true,
    }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["home"] });
      if (!r.dates.length) {
        toast(r.alreadyCovered.length ? "Those days are already on Home." : "Nothing is planned for those days.", "neutral");
        return;
      }
      if (r.added + r.topped === 0) {
        toast("Nothing is planned for those days, so nothing was added.", "danger");
        return;
      }
      toast(`${r.added} added to Home${r.topped ? `, ${r.topped} topped up` : ""}.`, "ok");
      navigate(`/shopping/${r.listId}`);
    },
    onError: (e: Error) => toast(e.message, "danger"),
  });

  // `?generate=1` from NextCard, GettingStarted, or the Plan hand-off.
  useEffect(() => {
    if (params.get("generate") !== "1" || !today) return;
    const from = params.get("from");
    const to = params.get("to");
    params.delete("generate"); params.delete("from"); params.delete("to");
    setParams(params, { replace: true });
    quickMerge.mutate(from && to ? { from, to } : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, today]);

  if (!today) return <Loading />;
  if (!model) {
    if (status === "error") return <ErrorBox message={error ?? "Couldn't load the lists."} />;
    return <Loading label="Loading your lists…" />;
  }

  const lists = liveLists(model);
  const archived = archivedLists(model);
  const templates = templateLists(model);
  const home = homeListId(model);

  const createList = (title: string, icon: string | null) => {
    const listId = crypto.randomUUID();
    run([{ op: "list.create", listId, title, icon }], null);
    setCreating(false);
    navigate(`/shopping/${listId}`);
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Shopping"
        subtitle="Home is always there for everyday things; make a list for anything else."
        actions={
          <>
            <button className="btn-ghost" onClick={() => quickMerge.mutate(undefined)}
                    disabled={quickMerge.isPending}
                    title={`This week's ingredients (${formatDay(today)} – ${formatDay(addDays(today, 6))}) onto Home, staples skipped`}>
              <Icon name="calendar" className="h-4 w-4" />
              {quickMerge.isPending ? "Working it out…" : "This week's plan"}
            </button>
            <button className="btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" className="h-4 w-4" />
              New list
            </button>
            <ActionMenu items={[
              { label: "Stores", icon: "cart", onSelect: () => setSheet("stores"),
                hint: "Where things are bought, and each store's walking order" },
              { label: "Categories", icon: "pencil", onSelect: () => setSheet("categories") },
              { label: "Add the plan for other days…", icon: "calendarPlus", onSelect: () => setSheet("plan") },
            ]} />
          </>
        }
      />

      <div className="mb-3 flex items-center justify-between gap-2">
        <LiveBadge status={status} unsent={unsent} />
        {model.stores.length > 0 && (
          <span className="text-xs text-ink-3">
            Stores: {model.stores.map((s) => s.name).join(", ")}{" "}
            <button className="text-accent hover:underline" onClick={() => setSheet("stores")}>Edit</button>
          </span>
        )}
      </div>

      <div className="space-y-2">
        {lists.map((l) => (
          <ListRow key={l.id} list={l} model={model}
            onRename={() => setRenaming(l)}
            onDuplicate={() => {
              const newListId = crypto.randomUUID();
              run([{ op: "list.duplicate", listId: l.id, newListId, title: `${l.title} (copy)` }], null);
              toast(`Copied "${l.title}".`, "ok");
            }}
            onArchive={() => run([{ op: "list.archive", listId: l.id }], `Archived "${l.title}"`)}
          />
        ))}
      </div>

      {templates.length > 0 && (
        <div className="mt-8">
          <h2 className="label-mono mb-2">Templates</h2>
          <div className="card divide-y divide-rule-soft">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center gap-2 p-3 text-sm">
                <span aria-hidden>{t.icon ?? "📋"}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{t.title}</div>
                  <div className="truncate text-xs text-ink-3">
                    {itemsOf(model, t.id).map((i) => i.display_name).join(", ")}
                  </div>
                </div>
                {home && (
                  <button className="btn-ghost px-2 py-1"
                    onClick={() => run([{ op: "template.apply", templateId: t.id, listId: home }], null)}>
                    Add to Home
                  </button>
                )}
                <IconButton name="trash" tone="danger" label={`Delete template ${t.title}`}
                  onClick={() => run([{ op: "list.delete", listId: t.id }], null)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {archived.length > 0 && (
        <div className="mt-8">
          <button className="label-mono flex items-center gap-1 text-ink-3 hover:text-ink"
                  onClick={() => setShowArchive((v) => !v)} aria-expanded={showArchive}>
            <Icon name={showArchive ? "chevronDown" : "chevronRight"} className="h-3.5 w-3.5" />
            Archived ({archived.length})
          </button>
          {showArchive && (
            <div className="mt-2 space-y-2">
              {archived.map((l) => (
                <ArchivedRow key={l.id} list={l}
                  onRestore={() => run([{ op: "list.restore", listId: l.id }], `Restored "${l.title}"`)}
                  onDelete={() => run([{ op: "list.delete", listId: l.id }], null)} />
              ))}
            </div>
          )}
        </div>
      )}

      {creating && <ListNameModal title="New list" confirm="Create"
        onClose={() => setCreating(false)} onSave={createList} />}
      {renaming && <ListNameModal title="Rename list" confirm="Save"
        initialTitle={renaming.title} initialIcon={renaming.icon}
        onClose={() => setRenaming(null)}
        onSave={(title, icon) => {
          run([{ op: "list.update", listId: renaming.id, title, icon }], "Renamed");
          setRenaming(null);
        }} />}
      {sheet === "stores" && <StoresModal model={model} run={run} onClose={() => setSheet(null)} />}
      {sheet === "categories" && <CategoriesModal model={model} run={run} onClose={() => setSheet(null)} />}
      {sheet === "plan" && <PlanMergeModal model={model} today={today} onClose={() => setSheet(null)} />}
      {bar}
    </div>
  );
}

function ListRow({ list, model, onRename, onDuplicate, onArchive }: {
  list: LiveList; model: Model;
  onRename: () => void; onDuplicate: () => void; onArchive: () => void;
}) {
  const c = counts(model, list.id);
  const home = list.kind === "home";
  const latest = itemsOf(model, list.id)
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];
  const shoppers = sessionsOn(model, list.id);
  const shares = sharesOf(model, list.id);
  return (
    <div className={`card flex items-center gap-3 p-3 ${home ? "border-accent/30" : ""}`}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-paper-3 text-xl" aria-hidden>
        {list.icon ?? (home ? "🏠" : "🛒")}
      </span>
      <div className="min-w-0 flex-1">
        <Link to={`/shopping/${list.id}`} className="font-medium hover:text-accent">
          {list.title}
        </Link>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-3">
          <span>{c.needed ? `${c.needed} to buy` : "Nothing needed"}</span>
          {c.got > 0 && <span>· {c.got} in the trolley</span>}
          {latest && (
            <span>· {memberName(model, latest.updated_by ?? latest.added_by)} {timeAgo(latest.updated_at)}</span>
          )}
          {shoppers.map((s) => (
            <span key={s.id} className="chip border-accent/40 text-accent">
              {memberName(model, s.member_sub)} shopping{s.store_id ? ` at ${storeName(model, s.store_id)}` : ""}
            </span>
          ))}
          {shares.length > 0 && <span className="chip">shared with {shares.map((s) => s.label).join(", ")}</span>}
          {list.is_stale && <span className="chip border-warn/40 text-warn">plan changed</span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center">
        <IconButton name="pencil" label={`Rename ${list.title}`} onClick={onRename} />
        <IconButton name="copy" label={`Duplicate ${list.title}`} onClick={onDuplicate} />
        {!home && <IconButton name="archive" label={`Archive ${list.title}`} onClick={onArchive} />}
        <Link className={`${home ? "btn-primary" : "btn-ghost"} ml-1`} to={`/shopping/${list.id}/mode`}>
          Shop
        </Link>
      </div>
    </div>
  );
}

function ArchivedRow({ list, onRestore, onDelete }: {
  list: LiveList; onRestore: () => void; onDelete: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="card flex items-center gap-3 p-3 text-sm opacity-80">
      <span aria-hidden>{list.icon ?? "🛒"}</span>
      <span className="min-w-0 flex-1 truncate">{list.title}</span>
      {confirming ? (
        <>
          <span className="text-xs text-danger">Delete for good?</span>
          <button className="btn-danger" onClick={onDelete}>Delete</button>
          <button className="btn-ghost" onClick={() => setConfirming(false)}>Keep</button>
        </>
      ) : (
        <>
          <button className="btn-ghost" onClick={onRestore}>Restore</button>
          <IconButton name="trash" tone="danger" label={`Delete ${list.title}`}
                      onClick={() => setConfirming(true)} />
        </>
      )}
    </div>
  );
}

const ICONS = ["🛒", "🏠", "🎉", "🥩", "🥦", "🍷", "🎂", "🏕️", "🧴", "👶", "🐾", "🕯️"];

function ListNameModal({ title, confirm, initialTitle = "", initialIcon = null, onClose, onSave }: {
  title: string; confirm: string; initialTitle?: string; initialIcon?: string | null;
  onClose: () => void; onSave: (title: string, icon: string | null) => void;
}) {
  const [name, setName] = useState(initialTitle);
  const [icon, setIcon] = useState<string | null>(initialIcon ?? "🛒");
  return (
    <Modal title={title} onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) onSave(name.trim(), icon);
      }}>
        <label className="block">
          <span className="text-sm">Name</span>
          <input className="input mt-1" value={name} autoFocus maxLength={200}
                 placeholder="Costco run, Friday dinner, Camping…"
                 onChange={(e) => setName(e.target.value)} />
        </label>
        <div>
          <span className="text-sm">Icon</span>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {ICONS.map((i) => (
              <button key={i} type="button" aria-pressed={icon === i}
                      className={`h-10 w-10 rounded-lg border text-xl transition ${
                        icon === i ? "border-accent bg-accent/15" : "border-rule-soft hover:bg-paper-3"}`}
                      onClick={() => setIcon(i)}>
                {i}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-ghost flex-1" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary flex-1" disabled={!name.trim()}>{confirm}</button>
        </div>
      </form>
    </Modal>
  );
}

export function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86_400)} d ago`;
}

/**
 * The regenerate flow: PREVIEW first, apply second.
 *
 * The user is often mid-shop. Showing exactly what would change — and defaulting
 * removals to off — is the difference between a helpful refresh and losing
 * somebody's ticked list.
 */
export function RegeneratePanel({ listId, onDone }: { listId: string; onDone: () => void }) {
  const [diff, setDiff] = useState<RegenerateDiff | null>(null);
  const [removeStale, setRemoveStale] = useState(false);

  const preview = useMutation({
    mutationFn: () => ShoppingApi.preview(listId),
    onSuccess: (r) => setDiff(r.diff),
  });
  const apply = useMutation({
    mutationFn: () => ShoppingApi.regenerate(listId, removeStale),
    onSuccess: onDone,
  });

  if (!diff) {
    return (
      <button className="btn-ghost" onClick={() => preview.mutate()} disabled={preview.isPending}>
        {preview.isPending ? "Checking…" : "See what changed"}
      </button>
    );
  }

  const nothing = !diff.added.length && !diff.updated.length && !diff.removed.length;

  return (
    <div className="card space-y-3 p-4 text-sm">
      {nothing ? (
        <p>Nothing has changed — your list already matches the plan.</p>
      ) : (
        <>
          {diff.added.length > 0 && (
            <div>
              <p className="font-medium text-accent">{diff.added.length} to add</p>
              <ul className="text-ink-2">
                {diff.added.map((a, i) => <li key={i}>+ {a.quantity ?? ""} {a.unit ?? ""} {a.displayName}</li>)}
              </ul>
            </div>
          )}
          {diff.updated.length > 0 && (
            <div>
              <p className="font-medium">{diff.updated.length} quantity change{diff.updated.length === 1 ? "" : "s"}</p>
              <ul className="text-ink-2">
                {diff.updated.map((u) => (
                  <li key={u.id}>{u.displayName}: {u.from} → {u.to} {u.unit ?? ""}</li>
                ))}
              </ul>
            </div>
          )}
          {diff.removed.length > 0 && (
            <div>
              <p className="font-medium text-warn">{diff.removed.length} no longer needed</p>
              <ul className="text-ink-2">
                {diff.removed.map((r) => (
                  <li key={r.id}>
                    {r.displayName}{r.isChecked && " — you've already ticked this"}
                  </li>
                ))}
              </ul>
              <label className="mt-2 flex items-center gap-2">
                <input type="checkbox" checked={removeStale}
                  onChange={(e) => setRemoveStale(e.target.checked)} />
                Remove them from the list
              </label>
            </div>
          )}
          {diff.preservedManual > 0 && (
            <p className="text-ink-3">
              {diff.preservedManual} hand-added item{diff.preservedManual === 1 ? "" : "s"} will be kept.
            </p>
          )}
        </>
      )}
      <div className="flex gap-2">
        <button className="btn-primary" onClick={() => apply.mutate()} disabled={apply.isPending}>
          {nothing ? "Mark up to date" : "Apply"}
        </button>
        <button className="btn-ghost" onClick={() => setDiff(null)}>Cancel</button>
      </div>
    </div>
  );
}
